/**
 * Content module — surface 2.1 "All projects" (the portfolio board).
 *
 * Every figure, name, id, route and date the board renders is resolved HERE
 * from `@/demo/data`; the page component holds no literals (build brief §0a).
 * Facts the deck states are read from PROJECTS / PORTFOLIO / PHASES / OFFICES;
 * the three won-not-started engagements are deck-implied but unnamed, so they
 * are invented ONCE below and reused by anyone who needs them.
 */
import {
  OFFICES,
  PHASES,
  PORTFOLIO,
  PROJECTS,
  personById,
  thinSpace,
  type Health,
  type OfficeCode,
  type PhaseKey,
  type Project,
} from '@/demo/data'
import type { StatusTone } from '@/components/px/StatusPill'

/* ── chrome ─────────────────────────────────────────────────────────────── */

export const MODULE_LABEL = 'PX · Projects'
export const BREADCRUMB = 'Portfolio › All projects'
export const PAGE_TITLE = 'All projects'
export const PAGE_LEDE =
  'Every engagement on one board, positioned by lifecycle phase. Colour is health, not activity — the flag comes from a slipping gate, never from a stale status.'

/** "18 ACTIVE · 4 OFFICES" — composed, never typed. */
export const CONTEXT_PILL = `${PORTFOLIO.active} ACTIVE · ${OFFICES.length} OFFICES`

/* ── formatting (defined once, used by every cell on this surface) ──────── */

/**
 * Contract money in the board's shorthand: 480_000 → "€480k",
 * 2_900_000 → "€2.9m". Sub-thousand values keep thin-space thousands so the
 * column stays tabular.
 */
export const eur = (amount: number): string => {
  if (amount >= 1_000_000) {
    const millions = (amount / 1_000_000).toFixed(1).replace(/\.0$/, '')
    return `€${millions}m`
  }
  if (amount >= 1_000) return `€${Math.round(amount / 1_000)}k`
  return `€${thinSpace(amount)}`
}

/** ISO day → "12 Nov 2026". Fixed input, so no clock is read. */
export const dayLabel = (iso: string): string =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  })

/* ── health + phase lookups ─────────────────────────────────────────────── */

/** Health is colour AND label — the pill tone never carries the meaning alone. */
export const healthTone: Record<Health, StatusTone> = {
  'On track': 'done',
  'At risk': 'review',
  Blocked: 'blocked',
}

/** Exception weight is monotonic with severity: blocked outranks at-risk. */
export const healthRowClass: Record<Health, string> = {
  'On track': '',
  'At risk': 'bg-review-bg/60',
  Blocked: 'bg-blocked-bg',
}

export const healthRuleClass: Record<Health, string> = {
  'On track': 'border-l-[3px] border-l-transparent',
  'At risk': 'border-l-[3px] border-l-review/60',
  Blocked: 'border-l-[3px] border-l-blocked',
}

const phaseName = (key: PhaseKey): string => {
  const phase = PHASES.find((p) => p.key === key)
  if (!phase) throw new Error(`Unknown phase: ${key}`)
  return phase.name
}

const officeLabel = (code: OfficeCode): string => {
  const office = OFFICES.find((o) => o.code === code)
  if (!office) throw new Error(`Unknown office: ${code}`)
  return `${office.code} · ${office.city}`
}

/* ── routes ─────────────────────────────────────────────────────────────── */

export const ROUTES = {
  clients: '/clients',
  templates: '/library/templates',
  portfolioHealth: '/projects/health',
  project: (id: string) => `/project/${id}`,
  person: (id: string) => `/resources/people/${id}`,
} as const

/**
 * Only the hero engagement has a built detail view this round; the rest route
 * to portfolio health rather than to a project page that would be a lie.
 */
export const NON_HERO_LINK_TITLE = 'detail view follows the hero project this round'
export const FOLLOWED_LABEL = 'followed'

/* ── board rows ─────────────────────────────────────────────────────────── */

export interface BoardRow {
  id: string
  client: string
  /** "Odoo 19 · Retail" */
  subLine: string
  href: string
  linkTitle?: string
  followed: boolean
  office: string
  phase: string
  health: Health
  lead: { id: string; name: string; initial: string; href: string }
  contract: string
  goLive: string
}

const toRow = (project: Project): BoardRow => {
  const lead = personById(project.leadId)
  const followed = project.followed === true
  return {
    id: project.id,
    client: project.client,
    subLine: `Odoo ${project.odooVersion} · ${project.vertical}`,
    href: followed ? ROUTES.project(project.id) : ROUTES.portfolioHealth,
    ...(followed ? {} : { linkTitle: NON_HERO_LINK_TITLE }),
    followed,
    office: officeLabel(project.office),
    phase: phaseName(project.phase),
    health: project.health,
    lead: { id: lead.id, name: lead.name, initial: lead.initial, href: ROUTES.person(lead.id) },
    contract: eur(project.contractValue),
    goLive: dayLabel(project.goLive),
  }
}

/** Delivery engagements — everything not yet handed to AMS. */
export const ACTIVE_ROWS: readonly BoardRow[] = PROJECTS.filter((p) => p.phase !== 'maintain').map(toRow)

/** The AMS contracts seeded on the board; the register carries the rest. */
export const AMS_ROWS: readonly BoardRow[] = PROJECTS.filter((p) => p.phase === 'maintain').map(toRow)

export const COLUMNS = [
  { key: 'project', label: 'Project', numeric: false },
  { key: 'office', label: 'Office', numeric: false },
  { key: 'phase', label: 'Phase', numeric: false },
  { key: 'health', label: 'Health', numeric: false },
  { key: 'lead', label: 'Lead', numeric: false },
  { key: 'contract', label: 'Contract €', numeric: true },
  { key: 'goLive', label: 'Go-live', numeric: false },
] as const

export const TABLE_CAPTION = 'Portfolio board — one row per engagement, positioned by lifecycle phase.'

/* ── tabs ───────────────────────────────────────────────────────────────── */

export type BoardTabKey = 'active' | 'ams' | 'won'

export interface BoardTab {
  key: BoardTabKey
  label: string
  count: number
}

export const BOARD_TABS: readonly BoardTab[] = [
  { key: 'active', label: 'Active', count: PORTFOLIO.active },
  { key: 'ams', label: 'AMS', count: PORTFOLIO.ams },
  { key: 'won', label: 'Won, not started', count: PORTFOLIO.wonNotStarted },
]

export const DEFAULT_TAB: BoardTabKey = 'active'

/** The AMS tab is honest about what it is not showing. */
export const AMS_NOTE = {
  before: `The board seeds ${AMS_ROWS.length} of ${PORTFOLIO.ams} AMS contracts. The other ${
    PORTFOLIO.ams - AMS_ROWS.length
  } run on the quarterly gate and live on `,
  link: { label: 'Clients & AMS', to: ROUTES.clients },
  after: ', where the renewal and upgrade columns belong.',
} as const

/* ── won, not started (deck-implied, named once here) ───────────────────── */

export interface WonNotStarted {
  id: string
  client: string
  office: string
  vertical: string
  contractType: string
  status: string
  note: string
}

export const WON_NOT_STARTED_STATUS = 'won · start pending'

export const WON_NOT_STARTED: readonly WonNotStarted[] = [
  {
    id: 'de-koning-groep',
    client: 'De Koning Groep',
    office: officeLabel('NL'),
    vertical: 'Wholesale',
    contractType: 'Fixed-scope rollout',
    status: WON_NOT_STARTED_STATUS,
    note: 'signed; waiting on the client to name a sponsor',
  },
  {
    id: 'verhaegen-textiel',
    client: 'Verhaegen Textiel',
    office: officeLabel('BE'),
    vertical: 'Manufacturing',
    contractType: 'Fixed-scope rollout',
    status: WON_NOT_STARTED_STATUS,
    note: 'signed; kickoff pencilled after their stock count',
  },
  {
    id: 'maison-perrin',
    client: 'Maison Perrin',
    office: officeLabel('FR'),
    vertical: 'Food service',
    contractType: 'Time & materials',
    status: WON_NOT_STARTED_STATUS,
    note: 'signed; discovery slot held for the Lyon team',
  },
]

export const WON_NOT_STARTED_PANEL = {
  title: `${PORTFOLIO.wonNotStarted} engagements are won but not yet started`,
  body: 'Nothing has a phase, a gate or a burn yet, so there is no row to draw. They enter the board on the day discovery opens.',
} as const

/* ── group-by chips (withheld this round, stated plainly) ───────────────── */

export const GROUP_CHIPS: readonly string[] = ['Group by: phase', 'All offices']
export const GROUP_CHIPS_TITLE = 'grouping is withheld this round'

/* ── KPI row ────────────────────────────────────────────────────────────── */

export interface KpiSpec {
  key: string
  label: string
  figure: string
  qualifier: { before: string; link?: { label: string; to: string }; after?: string }
}

export const BOARD_KPIS: readonly KpiSpec[] = [
  {
    key: 'in-delivery',
    label: 'In delivery',
    figure: String(PORTFOLIO.active),
    qualifier: { before: `${eur(PORTFOLIO.contractedValue)} contracted` },
  },
  {
    key: 'gates-due',
    label: 'Gates due this week',
    figure: String(PORTFOLIO.gatesDueThisWeek),
    qualifier: { before: `${PORTFOLIO.gatesNeedingClientSignature} need a client signature` },
  },
  {
    key: 'at-risk',
    label: 'At risk / blocked',
    figure: String(PORTFOLIO.atRiskOrBlocked),
    qualifier: { before: 'all on client-side input' },
  },
  {
    key: 'reuse-rate',
    label: 'Reuse rate',
    figure: `${PORTFOLIO.reuseRatePct}%`,
    qualifier: {
      before: 'scope met from ',
      link: { label: 'the library', to: ROUTES.templates },
      after: '',
    },
  },
]

/* ── density note ───────────────────────────────────────────────────────── */

export const DENSITY_NOTE = {
  before: `The board draws the ${PROJECTS.length} seeded engagements of ${PORTFOLIO.active} active and ${PORTFOLIO.ams} AMS; the tab counts and `,
  link: { label: 'the clients register', to: ROUTES.clients },
  after: ` carry the rest. Grouping and the office filter are withheld this round — ${PORTFOLIO.active} rows sort by phase and health without them.`,
} as const
